import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  LogOut,
  CalendarDays,
  Clock,
  CheckCircle2,
  Wallet,
  Eye,
  Trash2,
  Plus,
  Star,
  ExternalLink,
  Loader2,
  Search,
  KeyRound,
  Pencil,
  Download,
  UploadCloud,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah } from "@/lib/data";

const STATUSES = [
  "Menunggu Konfirmasi Admin",
  "Terkonfirmasi",
  "Sedang Diproses",
  "Selesai",
  "Dibatalkan",
];

const STATUS_STYLE = {
  "Menunggu Konfirmasi Admin": "bg-[#D97706]/10 text-[#D97706]",
  Terkonfirmasi: "bg-[#2E7D32]/10 text-[#2E7D32]",
  "Sedang Diproses": "bg-[#C5A059]/15 text-[#A8833E]",
  Selesai: "bg-[#6E7E65]/15 text-[#4c5a44]",
  Dibatalkan: "bg-red-100 text-red-600",
};

const fileUrl = (path) => (path ? `${BACKEND_URL}/api/files/${path}` : null);

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, dp_revenue: 0 });
  const [detail, setDetail] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [tForm, setTForm] = useState({ name: "", event: "", message: "", rating: 5 });
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Semua");
  const [packages, setPackages] = useState([]);
  const [pkgEditing, setPkgEditing] = useState(null);
  const [pkgForm, setPkgForm] = useState({ name: "", tagline: "", price: "", photo: "", description: "", featuresText: "" });
  const [savingPkg, setSavingPkg] = useState(false);
  const [pwForm, setPwForm] = useState({ old: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [gForm, setGForm] = useState({ category: "Pelaminan", title: "", image: "" });
  const [gEditing, setGEditing] = useState(null);
  const [savingG, setSavingG] = useState(false);
  const [contentDraft, setContentDraft] = useState({});
  const [savingContent, setSavingContent] = useState(false);

  const load = async () => {
    try {
      const [b, s] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/stats"),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch {
      toast.error("Gagal memuat data");
    }
  };

  const loadTestimonials = async () => {
    const r = await api.get("/testimonials");
    setTestimonials(r.data);
  };

  useEffect(() => {
    load();
    loadTestimonials();
    loadPackages();
    loadGallery();
    loadContentDraft();
  }, []);

  const updateStatus = async (code, status) => {
    try {
      await api.patch(`/admin/bookings/${code}/status`, { status });
      toast.success("Status diperbarui");
      setBookings((prev) => prev.map((b) => (b.booking_code === code ? { ...b, status } : b)));
      load();
      if (detail?.booking_code === code) setDetail((d) => ({ ...d, status }));
    } catch {
      toast.error("Gagal memperbarui status");
    }
  };

  const addTestimonial = async (e) => {
    e.preventDefault();
    if (!tForm.name || !tForm.event || !tForm.message) {
      toast.error("Lengkapi semua field testimoni");
      return;
    }
    setAdding(true);
    try {
      await api.post("/admin/testimonials", tForm);
      toast.success("Testimoni ditambahkan");
      setTForm({ name: "", event: "", message: "", rating: 5 });
      loadTestimonials();
    } catch {
      toast.error("Gagal menambah testimoni");
    } finally {
      setAdding(false);
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Testimoni dihapus");
      setTestimonials((prev) => prev.filter((t) => t.id !== id));
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const loadPackages = async () => {
    const r = await api.get("/packages");
    setPackages(r.data);
  };

  const resetPkgForm = () => {
    setPkgEditing(null);
    setPkgForm({ name: "", tagline: "", price: "", photo: "", description: "", featuresText: "" });
  };

  const editPackage = (p) => {
    setPkgEditing(p.id);
    setPkgForm({
      name: p.name,
      tagline: p.tagline || "",
      price: p.price,
      photo: p.photo || "",
      description: p.description || "",
      featuresText: (p.features || []).join("\n"),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const savePackage = async (e) => {
    e.preventDefault();
    if (!pkgForm.name || !pkgForm.price) {
      toast.error("Nama dan harga wajib diisi");
      return;
    }
    setSavingPkg(true);
    const payload = {
      name: pkgForm.name,
      tagline: pkgForm.tagline,
      price: Number(pkgForm.price),
      photo: pkgForm.photo,
      description: pkgForm.description,
      features: pkgForm.featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (pkgEditing) await api.put(`/admin/packages/${pkgEditing}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success("Paket disimpan");
      resetPkgForm();
      loadPackages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan paket");
    } finally {
      setSavingPkg(false);
    }
  };

  const deletePackage = async (id) => {
    try {
      await api.delete(`/admin/packages/${id}`);
      toast.success("Paket dihapus");
      setPackages((prev) => prev.filter((p) => p.id !== id));
    } catch {
      toast.error("Gagal menghapus paket");
    }
  };

  const movePackage = async (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= packages.length) return;
    const arr = [...packages];
    [arr[index], arr[j]] = [arr[j], arr[index]];
    setPackages(arr);
    try {
      await api.put("/admin/packages-reorder", { ids: arr.map((p) => p.id) });
      toast.success("Urutan paket diperbarui");
    } catch {
      toast.error("Gagal mengubah urutan");
      loadPackages();
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next.length < 6) {
      toast.error("Password baru minimal 6 karakter");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { old_password: pwForm.old, new_password: pwForm.next });
      toast.success("Password berhasil diganti");
      setPwForm({ old: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengganti password");
    } finally {
      setSavingPw(false);
    }
  };

  const loadGallery = async () => {
    const r = await api.get("/gallery");
    setGallery(r.data);
  };

  const loadContentDraft = async () => {
    const r = await api.get("/content");
    setContentDraft(r.data || {});
  };

  const resetGForm = () => {
    setGEditing(null);
    setGForm({ category: "Pelaminan", title: "", image: "" });
  };

  const editGalleryItem = (g) => {
    setGEditing(g.id);
    setGForm({ category: g.category, title: g.title, image: g.image });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveGallery = async (e) => {
    e.preventDefault();
    if (!gForm.title || !gForm.image) {
      toast.error("Judul dan gambar wajib diisi");
      return;
    }
    setSavingG(true);
    try {
      if (gEditing) await api.put(`/admin/gallery/${gEditing}`, gForm);
      else await api.post("/admin/gallery", gForm);
      toast.success("Galeri disimpan");
      resetGForm();
      loadGallery();
    } catch {
      toast.error("Gagal menyimpan galeri");
    } finally {
      setSavingG(false);
    }
  };

  const deleteGallery = async (id) => {
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Item galeri dihapus");
      setGallery((prev) => prev.filter((g) => g.id !== id));
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const setSection = (section, k, v) =>
    setContentDraft((d) => ({ ...d, [section]: { ...(d[section] || {}), [k]: v } }));
  const setHeroStat = (i, k, v) =>
    setContentDraft((d) => {
      const stats = [...((d.hero && d.hero.stats) || [])];
      stats[i] = { ...stats[i], [k]: v };
      return { ...d, hero: { ...(d.hero || {}), stats } };
    });
  const setAboutValue = (i, k, v) =>
    setContentDraft((d) => {
      const values = [...((d.about && d.about.values) || [])];
      values[i] = { ...values[i], [k]: v };
      return { ...d, about: { ...(d.about || {}), values } };
    });
  const setBank = (i, k, v) =>
    setContentDraft((d) => {
      const banks = [...((d.payment && d.payment.banks) || [])];
      banks[i] = { ...banks[i], [k]: v };
      return { ...d, payment: { ...(d.payment || {}), banks } };
    });
  const addBank = () =>
    setContentDraft((d) => ({
      ...d,
      payment: { ...(d.payment || {}), banks: [...((d.payment && d.payment.banks) || []), { bank: "", account: "", holder: "" }] },
    }));
  const removeBank = (i) =>
    setContentDraft((d) => ({
      ...d,
      payment: { ...(d.payment || {}), banks: ((d.payment && d.payment.banks) || []).filter((_, idx) => idx !== i) },
    }));

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await api.put("/admin/content", contentDraft);
      toast.success("Konten website tersimpan");
    } catch {
      toast.error("Gagal menyimpan konten");
    } finally {
      setSavingContent(false);
    }
  };

  const exportCSV = () => {
    const headers = ["Booking ID", "Nama", "WhatsApp", "Email", "Jenis Acara", "Tanggal", "Waktu", "Jumlah Tamu", "Venue", "Alamat", "Paket", "Total", "DP", "Metode", "Status", "Dibuat"];
    const rows = bookings.map((b) => [b.booking_code, b.client.namaLengkap, b.client.whatsapp, b.client.email, b.event.jenisAcara, b.event.tanggalAcara, b.event.waktuAcara, b.event.jumlahTamu, b.location.namaTempat, b.location.alamatLengkap, b.concept.paket, b.total_harga, b.dp_amount, b.payment_method || "", b.status, (b.created_at || "").slice(0, 10)]);
    const esc = (s) => `"${String(s == null ? "" : s).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `booking-ascarya-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredBookings = bookings.filter((b) => {
    const q = search.trim().toLowerCase();
    const match =
      !q ||
      b.booking_code.toLowerCase().includes(q) ||
      b.client.namaLengkap.toLowerCase().includes(q) ||
      (b.client.whatsapp || "").toLowerCase().includes(q);
    const st = statusFilter === "Semua" || b.status === statusFilter;
    return match && st;
  });

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const STAT_CARDS = [
    { label: "Total Booking", value: stats.total, icon: CalendarDays },
    { label: "Menunggu Konfirmasi", value: stats.pending, icon: Clock },
    { label: "Terkonfirmasi", value: stats.confirmed, icon: CheckCircle2 },
    { label: "Total DP Masuk", value: formatRupiah(stats.dp_revenue), icon: Wallet },
  ];

  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      <header className="bg-[#1C1917] text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C5A059]" />
            <div>
              <div className="font-serif text-lg font-semibold">Ascarya Dekorasi</div>
              <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#C5A059]">
                Admin Panel
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-white/60 hidden sm:block">
              {admin?.email}
            </span>
            <button
              onClick={doLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-xs tracking-widest uppercase hover:bg-white hover:text-[#1C1917] transition-all"
            >
              <LogOut className="w-4 h-4" /> Keluar
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {STAT_CARDS.map((c) => (
            <div key={c.label} className="bg-white rounded-2xl border border-[#E5D7C5] p-5" data-testid={`stat-${c.label.split(" ")[0].toLowerCase()}`}>
              <c.icon className="w-6 h-6 text-[#C5A059]" />
              <div className="mt-3 font-serif text-2xl font-semibold text-[#1C1917]">
                {c.value}
              </div>
              <div className="text-[11px] tracking-wide uppercase text-[#786F66]">
                {c.label}
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6">
          {[
            ["bookings", "Booking"],
            ["packages", "Paket"],
            ["gallery", "Galeri"],
            ["content", "Konten"],
            ["testimonials", "Testimoni"],
            ["settings", "Pengaturan"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`px-6 py-2.5 rounded-full text-xs font-semibold tracking-widest uppercase border transition-all ${
                tab === k
                  ? "bg-[#1C1917] text-white border-[#1C1917]"
                  : "bg-white text-[#4A443F] border-[#E5D7C5] hover:border-[#C5A059]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-[#967C52] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input className="pl-9" placeholder="Cari kode booking / nama / WhatsApp" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="booking-search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="sm:w-64" data-testid="booking-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Semua", ...STATUSES].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={exportCSV} data-testid="export-csv-button" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-[#E5D7C5] text-xs font-semibold tracking-widest uppercase text-[#4A443F] hover:border-[#C5A059] hover:text-[#C5A059] transition-all">
              <Download className="w-4 h-4" /> Ekspor CSV
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-[#E5D7C5] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-bookings-table">
                <thead>
                  <tr className="bg-[#F7F0E6] text-left">
                    {["Booking ID", "Client", "Tanggal", "Paket", "DP", "Status", ""].map((h) => (
                      <th key={h} className="px-4 py-3 font-mono text-[10px] tracking-widest uppercase text-[#967C52]">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-[#786F66]">
                        Belum ada booking masuk.
                      </td>
                    </tr>
                  )}
                  {filteredBookings.map((b) => (
                    <tr key={b.booking_code} className="border-t border-[#E5D7C5]" data-testid={`booking-row-${b.booking_code}`}>
                      <td className="px-4 py-3 font-mono text-[#1C1917]">{b.booking_code}</td>
                      <td className="px-4 py-3">{b.client.namaLengkap}</td>
                      <td className="px-4 py-3">{b.event.tanggalAcara}</td>
                      <td className="px-4 py-3">{b.concept.paket}</td>
                      <td className="px-4 py-3">{formatRupiah(b.dp_amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[b.status] || ""}`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setDetail(b)}
                          data-testid={`view-booking-${b.booking_code}`}
                          className="p-2 rounded-lg border border-[#E5D7C5] hover:border-[#C5A059] text-[#C5A059]"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}

        {tab === "testimonials" && (
          <div className="grid lg:grid-cols-3 gap-6">
            <form onSubmit={addTestimonial} className="bg-white rounded-2xl border border-[#E5D7C5] p-6 h-fit" data-testid="testimonial-form">
              <h3 className="font-serif text-xl text-[#1C1917] mb-4">Tambah Testimoni</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Nama</Label>
                  <Input className="mt-1.5" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} data-testid="testimonial-name-input" placeholder="Rani & Dimas" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Acara / Lokasi</Label>
                  <Input className="mt-1.5" value={tForm.event} onChange={(e) => setTForm({ ...tForm, event: e.target.value })} data-testid="testimonial-event-input" placeholder="Wedding, Bali" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Rating</Label>
                  <Select value={String(tForm.rating)} onValueChange={(v) => setTForm({ ...tForm, rating: Number(v) })}>
                    <SelectTrigger className="mt-1.5" data-testid="testimonial-rating-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} Bintang</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Pesan</Label>
                  <Textarea className="mt-1.5" value={tForm.message} onChange={(e) => setTForm({ ...tForm, message: e.target.value })} data-testid="testimonial-message-input" placeholder="Dekorasinya sangat memukau..." />
                </div>
                <button type="submit" disabled={adding} data-testid="testimonial-submit-button" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C5A059] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[#A8833E] transition-all disabled:opacity-60">
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Tambah</>}
                </button>
              </div>
            </form>

            <div className="lg:col-span-2 space-y-4">
              {testimonials.length === 0 && (
                <div className="bg-white rounded-2xl border border-[#E5D7C5] p-10 text-center text-[#786F66]">
                  Belum ada testimoni.
                </div>
              )}
              {testimonials.map((t) => (
                <div key={t.id} className="bg-white rounded-2xl border border-[#E5D7C5] p-5 flex justify-between gap-4" data-testid={`admin-testimonial-${t.id}`}>
                  <div>
                    <div className="flex gap-1 mb-1">
                      {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#C5A059] text-[#C5A059]" />)}
                    </div>
                    <p className="text-sm text-[#4A443F] italic">"{t.message}"</p>
                    <div className="mt-2 text-sm font-semibold text-[#1C1917]">{t.name}</div>
                    <div className="text-xs text-[#967C52]">{t.event}</div>
                  </div>
                  <button onClick={() => deleteTestimonial(t.id)} data-testid={`delete-testimonial-${t.id}`} className="p-2 h-fit rounded-lg border border-[#E5D7C5] hover:border-red-400 hover:text-red-500 text-[#786F66]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "packages" && (
          <div className="grid lg:grid-cols-3 gap-6">
            <form onSubmit={savePackage} className="bg-white rounded-2xl border border-[#E5D7C5] p-6 h-fit" data-testid="package-form">
              <h3 className="font-serif text-xl text-[#1C1917] mb-4">{pkgEditing ? "Edit Paket" : "Tambah Paket"}</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Nama Paket</Label>
                  <Input className="mt-1.5" value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} data-testid="package-name-input" placeholder="Paket Elegansi" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Tagline</Label>
                  <Input className="mt-1.5" value={pkgForm.tagline} onChange={(e) => setPkgForm({ ...pkgForm, tagline: e.target.value })} data-testid="package-tagline-input" placeholder="Sentuhan kemewahan" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Harga (Rp)</Label>
                  <Input className="mt-1.5" type="number" value={pkgForm.price} onChange={(e) => setPkgForm({ ...pkgForm, price: e.target.value })} data-testid="package-price-input" placeholder="15000000" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">URL Foto</Label>
                  <Input className="mt-1.5" value={pkgForm.photo} onChange={(e) => setPkgForm({ ...pkgForm, photo: e.target.value })} data-testid="package-photo-input" placeholder="https://..." />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Deskripsi</Label>
                  <Textarea className="mt-1.5" value={pkgForm.description} onChange={(e) => setPkgForm({ ...pkgForm, description: e.target.value })} data-testid="package-desc-input" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Fitur (satu per baris)</Label>
                  <Textarea className="mt-1.5" rows={5} value={pkgForm.featuresText} onChange={(e) => setPkgForm({ ...pkgForm, featuresText: e.target.value })} data-testid="package-features-input" placeholder={"Backdrop 6m\nFull fresh flower"} />
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={savingPkg} data-testid="package-save-button" className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C5A059] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[#A8833E] transition-all disabled:opacity-60">
                    {savingPkg ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Simpan</>}
                  </button>
                  {pkgEditing && (
                    <button type="button" onClick={resetPkgForm} data-testid="package-cancel-button" className="px-5 py-3 rounded-full border border-[#E5D7C5] text-[#4A443F] text-xs font-semibold tracking-widest uppercase hover:border-[#C5A059]">
                      Batal
                    </button>
                  )}
                </div>
              </div>
            </form>

            <div className="lg:col-span-2 space-y-4">
              {packages.map((p, idx) => (
                <div key={p.id} className="bg-white rounded-2xl border border-[#E5D7C5] p-5 flex justify-between gap-4" data-testid={`admin-package-${p.id}`}>
                  <div className="flex gap-4">
                    {p.photo && <img src={p.photo} alt={p.name} className="w-20 h-20 rounded-lg object-cover shrink-0" />}
                    <div>
                      <div className="font-serif text-lg text-[#1C1917]">{p.name}</div>
                      <div className="text-xs text-[#967C52]">{p.tagline}</div>
                      <div className="font-serif text-[#C5A059] font-semibold mt-1">{formatRupiah(p.price)}</div>
                      <div className="text-xs text-[#786F66] mt-1">{(p.features || []).length} fitur</div>
                    </div>
                  </div>
                  <div className="flex gap-2 h-fit">
                    <div className="flex flex-col gap-1">
                      <button onClick={() => movePackage(idx, -1)} disabled={idx === 0} data-testid={`move-up-package-${p.id}`} className="p-1.5 rounded-lg border border-[#E5D7C5] hover:border-[#C5A059] text-[#C5A059] disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => movePackage(idx, 1)} disabled={idx === packages.length - 1} data-testid={`move-down-package-${p.id}`} className="p-1.5 rounded-lg border border-[#E5D7C5] hover:border-[#C5A059] text-[#C5A059] disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                    </div>
                    <button onClick={() => editPackage(p)} data-testid={`edit-package-${p.id}`} className="p-2 rounded-lg border border-[#E5D7C5] hover:border-[#C5A059] text-[#C5A059] h-fit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => deletePackage(p.id)} data-testid={`delete-package-${p.id}`} className="p-2 rounded-lg border border-[#E5D7C5] hover:border-red-400 hover:text-red-500 text-[#786F66] h-fit"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div className="grid lg:grid-cols-3 gap-6">
            <form onSubmit={saveGallery} className="bg-white rounded-2xl border border-[#E5D7C5] p-6 h-fit" data-testid="gallery-form">
              <h3 className="font-serif text-xl text-[#1C1917] mb-4">{gEditing ? "Edit Foto" : "Tambah Foto Galeri"}</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Kategori</Label>
                  <Select value={gForm.category} onValueChange={(v) => setGForm({ ...gForm, category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="gallery-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Pelaminan", "Aisle & Arch", "Meja VVIP", "Photo Booth"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Judul</Label>
                  <Input className="mt-1.5" value={gForm.title} onChange={(e) => setGForm({ ...gForm, title: e.target.value })} data-testid="gallery-title-input" />
                </div>
                <ImageField label="Gambar" value={gForm.image} onChange={(url) => setGForm({ ...gForm, image: url })} testid="gallery-image" />
                <div className="flex gap-2">
                  <button type="submit" disabled={savingG} data-testid="gallery-save-button" className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C5A059] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[#A8833E] disabled:opacity-60">{savingG ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Simpan</>}</button>
                  {gEditing && <button type="button" onClick={resetGForm} className="px-5 py-3 rounded-full border border-[#E5D7C5] text-xs font-semibold tracking-widest uppercase">Batal</button>}
                </div>
              </div>
            </form>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
              {gallery.map((g) => (
                <div key={g.id} className="bg-white rounded-2xl border border-[#E5D7C5] overflow-hidden" data-testid={`admin-gallery-${g.id}`}>
                  <img src={g.image} alt={g.title} className="w-full h-36 object-cover" />
                  <div className="p-4 flex justify-between items-start gap-2">
                    <div>
                      <div className="text-xs text-[#967C52]">{g.category}</div>
                      <div className="font-serif text-[#1C1917]">{g.title}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => editGalleryItem(g)} data-testid={`edit-gallery-${g.id}`} className="p-2 rounded-lg border border-[#E5D7C5] hover:border-[#C5A059] text-[#C5A059]"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => deleteGallery(g.id)} data-testid={`delete-gallery-${g.id}`} className="p-2 rounded-lg border border-[#E5D7C5] hover:border-red-400 hover:text-red-500 text-[#786F66]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "content" && (
          <div className="space-y-6 max-w-3xl" data-testid="content-editor">
            <div className="flex items-center justify-between sticky top-0 bg-[#FAF6F0] py-2 z-10">
              <h3 className="font-serif text-xl text-[#1C1917]">Konten Website</h3>
              <button onClick={saveContent} disabled={savingContent} data-testid="save-content-button" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C5A059] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[#A8833E] disabled:opacity-60">{savingContent ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Semua"}</button>
            </div>

            <ContentGroup title="Brand">
              <CField label="Nama Brand" value={contentDraft.brand?.name || ""} onChange={(v) => setSection("brand", "name", v)} testid="content-brand-name" />
              <CField label="Tagline" value={contentDraft.brand?.tagline || ""} onChange={(v) => setSection("brand", "tagline", v)} />
            </ContentGroup>

            <ContentGroup title="Hero">
              <CField label="Badge" value={contentDraft.hero?.badge || ""} onChange={(v) => setSection("hero", "badge", v)} />
              <CField label="Judul Baris 1" value={contentDraft.hero?.line1 || ""} onChange={(v) => setSection("hero", "line1", v)} testid="content-hero-line1" />
              <CField label="Judul Baris 2" value={contentDraft.hero?.line2 || ""} onChange={(v) => setSection("hero", "line2", v)} />
              <CField label="Subtitle" textarea value={contentDraft.hero?.subtitle || ""} onChange={(v) => setSection("hero", "subtitle", v)} />
              <ImageField label="Gambar Hero" value={contentDraft.hero?.image || ""} onChange={(v) => setSection("hero", "image", v)} testid="content-hero-image" />
              <div className="grid grid-cols-3 gap-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="space-y-2">
                    <Input placeholder="Nilai" value={contentDraft.hero?.stats?.[i]?.value || ""} onChange={(e) => setHeroStat(i, "value", e.target.value)} />
                    <Input placeholder="Label" value={contentDraft.hero?.stats?.[i]?.label || ""} onChange={(e) => setHeroStat(i, "label", e.target.value)} />
                  </div>
                ))}
              </div>
            </ContentGroup>

            <ContentGroup title="Tentang Kami">
              <CField label="Judul" value={contentDraft.about?.title || ""} onChange={(v) => setSection("about", "title", v)} />
              <CField label="Cerita" textarea value={contentDraft.about?.story || ""} onChange={(v) => setSection("about", "story", v)} />
              <ImageField label="Gambar" value={contentDraft.about?.image || ""} onChange={(v) => setSection("about", "image", v)} />
              <div className="grid grid-cols-2 gap-3">
                <CField label="Stat Nilai" value={contentDraft.about?.statValue || ""} onChange={(v) => setSection("about", "statValue", v)} />
                <CField label="Stat Label" value={contentDraft.about?.statLabel || ""} onChange={(v) => setSection("about", "statLabel", v)} />
              </div>
              {[0, 1, 2].map((i) => (
                <div key={i} className="grid grid-cols-2 gap-3">
                  <Input placeholder="Judul nilai" value={contentDraft.about?.values?.[i]?.title || ""} onChange={(e) => setAboutValue(i, "title", e.target.value)} />
                  <Input placeholder="Deskripsi" value={contentDraft.about?.values?.[i]?.desc || ""} onChange={(e) => setAboutValue(i, "desc", e.target.value)} />
                </div>
              ))}
            </ContentGroup>

            <ContentGroup title="Kontak">
              <CField label="Alamat" value={contentDraft.contact?.alamat || ""} onChange={(v) => setSection("contact", "alamat", v)} testid="content-contact-alamat" />
              <CField label="WhatsApp (tampilan)" value={contentDraft.contact?.whatsapp || ""} onChange={(v) => setSection("contact", "whatsapp", v)} />
              <CField label="Nomor WhatsApp (link, mis. 628...)" value={contentDraft.contact?.whatsappNumber || ""} onChange={(v) => setSection("contact", "whatsappNumber", v)} />
              <CField label="Email" value={contentDraft.contact?.email || ""} onChange={(v) => setSection("contact", "email", v)} />
              <CField label="Instagram" value={contentDraft.contact?.instagram || ""} onChange={(v) => setSection("contact", "instagram", v)} />
              <CField label="Jam Operasional" value={contentDraft.contact?.jam || ""} onChange={(v) => setSection("contact", "jam", v)} />
              <CField label="Google Maps Embed URL" value={contentDraft.contact?.mapsEmbed || ""} onChange={(v) => setSection("contact", "mapsEmbed", v)} />
            </ContentGroup>

            <ContentGroup title="Pembayaran">
              <ImageField label="Gambar QRIS" value={contentDraft.payment?.qrisUrl || ""} onChange={(v) => setSection("payment", "qrisUrl", v)} testid="content-qris" />
              {(contentDraft.payment?.banks || []).map((bk, i) => (
                <div key={i} className="grid sm:grid-cols-4 gap-2 items-center">
                  <Input placeholder="Bank" value={bk.bank || ""} onChange={(e) => setBank(i, "bank", e.target.value)} />
                  <Input placeholder="No. Rekening" value={bk.account || ""} onChange={(e) => setBank(i, "account", e.target.value)} />
                  <Input placeholder="Atas Nama" value={bk.holder || ""} onChange={(e) => setBank(i, "holder", e.target.value)} />
                  <button type="button" onClick={() => removeBank(i)} className="px-3 py-2 rounded-lg border border-[#E5D7C5] text-red-500 text-xs">Hapus</button>
                </div>
              ))}
              <button type="button" onClick={addBank} data-testid="add-bank-button" className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-[#C5A059]"><Plus className="w-4 h-4" /> Tambah Rekening</button>
            </ContentGroup>
          </div>
        )}

        {tab === "settings" && (
          <div className="max-w-md">
            <form onSubmit={changePassword} className="bg-white rounded-2xl border border-[#E5D7C5] p-6" data-testid="password-form">
              <div className="flex items-center gap-2 mb-4">
                <KeyRound className="w-5 h-5 text-[#C5A059]" />
                <h3 className="font-serif text-xl text-[#1C1917]">Ganti Password</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Password Lama</Label>
                  <Input className="mt-1.5" type="password" value={pwForm.old} onChange={(e) => setPwForm({ ...pwForm, old: e.target.value })} data-testid="old-password-input" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Password Baru</Label>
                  <Input className="mt-1.5" type="password" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} data-testid="new-password-input" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-[#967C52]">Konfirmasi Password Baru</Label>
                  <Input className="mt-1.5" type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="confirm-password-input" />
                </div>
                <button type="submit" disabled={savingPw} data-testid="change-password-button" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C5A059] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[#A8833E] transition-all disabled:opacity-60">
                  {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Password"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl bg-[#FFFDF9] border-[#E5D7C5] max-h-[90vh] overflow-y-auto" data-testid="admin-booking-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#1C1917]">
                  {detail.booking_code}
                </DialogTitle>
              </DialogHeader>

              <div className="flex items-center gap-3 mb-2">
                <Label className="text-xs tracking-widest uppercase text-[#967C52]">Ubah Status</Label>
                <Select value={detail.status} onValueChange={(v) => updateStatus(detail.booking_code, v)}>
                  <SelectTrigger className="w-64" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Block title="Data Client" rows={[["Nama", detail.client.namaLengkap], ["WhatsApp", detail.client.whatsapp], ["Email", detail.client.email], ["Alamat", detail.client.alamat]]} />
                <Block title="Detail Acara" rows={[["Jenis", detail.event.jenisAcara], ["Tanggal", detail.event.tanggalAcara], ["Waktu", detail.event.waktuAcara], ["Tamu", detail.event.jumlahTamu]]} />
                <Block title="Lokasi" rows={[["Tempat", detail.location.namaTempat], ["Alamat", detail.location.alamatLengkap], ["Maps", detail.location.linkMaps || "-"]]} />
                <Block title="Paket & Pembayaran" rows={[["Paket", detail.concept.paket], ["Total", formatRupiah(detail.total_harga)], ["DP 50%", formatRupiah(detail.dp_amount)], ["Metode", detail.payment_method || "-"]]} />
                <Block title="Konsep" rows={[["Tema", detail.concept.tema || "-"], ["Warna", detail.concept.warnaDominan || "-"], ["Catatan", detail.concept.catatanTambahan || "-"]]} />
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mt-2">
                <PhotoLink label="Foto Lokasi" path={detail.location.fotoLokasi} />
                <PhotoLink label="Foto Referensi" path={detail.concept.fotoReferensi} />
                <PhotoLink label="Bukti Pembayaran" path={detail.payment_proof} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const CField = ({ label, value, onChange, textarea, testid }) => (
  <div>
    <Label className="text-xs tracking-widest uppercase text-[#967C52]">{label}</Label>
    {textarea ? (
      <Textarea className="mt-1.5" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    ) : (
      <Input className="mt-1.5" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    )}
  </div>
);

const ContentGroup = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-[#E5D7C5] p-6 space-y-4">
    <h4 className="font-serif text-lg text-[#1C1917]">{title}</h4>
    {children}
  </div>
);

const ImageField = ({ label, value, onChange, testid }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      onChange(`${BACKEND_URL}${data.url}`);
    } catch {
      toast.error("Gagal mengunggah gambar");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <Label className="text-xs tracking-widest uppercase text-[#967C52]">{label}</Label>
      <div className="mt-1.5 flex gap-2">
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="URL gambar" data-testid={testid} />
        <button type="button" onClick={() => ref.current?.click()} className="px-3 py-2 rounded-lg border border-[#E5D7C5] text-[#C5A059] shrink-0">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}</button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && up(e.target.files[0])} />
      </div>
      {value && <img src={value} alt="preview" className="mt-2 h-20 rounded-lg object-cover" />}
    </div>
  );
};

const Block = ({ title, rows }) => (
  <div className="rounded-xl border border-[#E5D7C5] bg-white p-4">
    <div className="font-mono text-[10px] tracking-widest uppercase text-[#C5A059] mb-2">{title}</div>
    <dl className="space-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3">
          <dt className="text-[#786F66] shrink-0">{k}</dt>
          <dd className="text-[#1C1917] font-medium text-right break-words">{v || "-"}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const PhotoLink = ({ label, path }) => {
  if (!path) return (
    <div className="rounded-xl border border-dashed border-[#E5D7C5] bg-white p-3 text-center text-xs text-[#786F66]">
      {label}: tidak ada
    </div>
  );
  const url = fileUrl(path);
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block rounded-xl border border-[#E5D7C5] bg-white overflow-hidden group" data-testid={`photo-${label.replace(/\s/g, "").toLowerCase()}`}>
      <img src={url} alt={label} className="w-full h-24 object-cover" />
      <div className="px-3 py-2 text-xs text-[#4A443F] flex items-center justify-between">
        {label} <ExternalLink className="w-3 h-3 text-[#C5A059]" />
      </div>
    </a>
  );
};
